import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import Popover from "@mui/material/Popover";
import BrandWheelLoader from "../../BrandWheelLoader";
import Tooltip from "@mui/material/Tooltip";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";

import { useBreakpoint } from "../../../utils/breakpoints";
import NotificationItem from "../NotificationItem";
import NotificationsEmptyState from "../EmptyState";
import { openReportForActor } from "../openReportForActor";
import TextView from "../../TextView";
import { colors } from "../../../utils";
// gordon-0831-header-bell-hover-circle: every sibling header nav icon (Home/
// Discover/Capture/Chat/Share) renders through the shared IconWrapper
// (rounded-square, 4px radius, NAV_ICON_HIGHLIGHT_BG hover background). The
// bell is a bare MUI IconButton, which defaults to a 50% (circular) radius —
// reuse the SAME shape + hover token here so the bell's hover affordance
// matches every other header icon instead of standing out as a circle.
import {
  HEADER_ICON_BUTTON_PADDING_PX,
  HEADER_ICON_BUTTON_SIZE,
  HEADER_ICON_GAP_PX,
  HEADER_ICON_SIZE,
  NAV_ICON_HIGHLIGHT_BG,
  headerMuiBadgeSlotProps,
} from "../../WheelGallery/Header/styles";
import {
  useInfiniteNotifications,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useDeleteNotification,
  useUnreadNotificationCount,
  resolveUnreadBadge,
} from "../useNotifications";
import {
  Panel,
  PanelHeader,
  HeaderLeft,
  UnreadPill,
  MarkAllButton,
  FeedBody,
  StateBox,
  RetryButton,
  PanelFooter,
  SeePreviousButton,
  PanelBottomBar,
  ViewAllButton,
} from "./NotificationCenter.styled";

// Bell entry point + accessible popover panel for the notification center.
// Rendered flag-gated (NOTIF_CENTER_ENABLED) inside GalleryHeader — off = not
// mounted. All server state flows through react-query.
//
// ISP-9999 (2026-08-27): QA-1014's "the dropdown IS the full list" theory
// didn't hold up — with count=426 across 43 pages, "See all" expanding pages
// into this same popover was never a real path to the tail of a user's
// history (no navigation, panel stays open, no modal; see routes.js for the
// measured evidence). "View All" (bell-panel-footer-placement-vs-deck: renamed
// from "See all" and moved into the bottom PanelBottomBar per the deck) now
// navigates to the routed /notifications page, which owns real "See More"
// pagination through every page. "See previous notifications" inside the
// scrollable feed still drives one extra page of in-place infinite scroll for
// a quick peek without leaving the panel — a distinct affordance from "View
// All", unaddressed by the deck note and left as-is.
function NotificationCenter() {
  const bellRef = useRef(null);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // mobile390-header-profilemenu-overflow: this bell is a persistent header
  // control that never folds into HeaderOverflowMenu (unlike SecondaryActions),
  // so its own footprint counts against the phone-width budget every time.
  // size="large" (MUI default 12px padding) was tuned for desktop; shrinking
  // to "medium" (8px padding) on phone claws back 8px next to the other
  // GalleryHeader/styles.js phone-band trims for the same measured overflow.
  const { isPhone } = useBreakpoint();

  const {
    data,
    isLoading,
    isError,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteNotifications();

  const markAll = useMarkAllNotificationsRead();
  const markOne = useMarkNotificationRead();
  const removeOne = useDeleteNotification();

  const { data: serverUnread } = useUnreadNotificationCount();

  // Flatten every accumulated page into one running list. Appending a page never
  // rebuilds the earlier rows, so the scroll position is preserved (no jump).
  const results = (data && Array.isArray(data.pages))
    ? data.pages.flatMap((pg) => (pg && pg.results) || [])
    : [];
  // The feed is page-limited, so counting unread rows in the loaded pages caps
  // the badge — resolveUnreadBadge() prefers the server total and falls back to
  // the loaded count only while that query is loading or if it errors.
  const unread = resolveUnreadBadge(serverUnread, results);
  const items = results;

  const openPanel = () => {
    setOpen(true);
    // Pull the freshest feed when the user actually looks (staleTime guards spam).
    refetch();
  };
  const closePanel = () => setOpen(false);

  // "View All" (bottom bar): navigate to the routed full-list page instead of
  // expanding pages into this popover — see the ISP-9999 note above the
  // component for why in-place expansion never actually reached the tail of
  // a 43-page history.
  const handleViewAll = () => {
    closePanel();
    navigate("/notifications");
  };

  // "See previous notifications" (bottom): the infinite-scroll driver. Appends
  // exactly one more page and CONTINUES from the current scroll position.
  const handleSeePrevious = () => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  };

  // Activating a row navigates away, so the panel must not stay floating over the
  // new route — hence a capture-phase dismiss on the whole feed.
  //
  // MEASURED live on web-stg 2026-07-28: that dismiss was unconditional
  // (`onClickCapture={closePanel}`), which also swallowed the per-row actor
  // actions (the compact Follow / Message overflow control). Capture runs BEFORE
  // the slot's own bubble-phase stopPropagation, so the panel unmounted on the
  // very click meant to open the menu: 4 such controls were present in the panel
  // and clicking one left `panelStillOpen:false, menuVisible:false` — the control
  // was unreachable from the bell panel entirely.
  //
  // So carve out that ONE subtree by its marker and keep capture for everything
  // else (row activation, avatar link dismiss exactly as before). The menu itself
  // renders in a MUI portal outside FeedBody, so choosing Follow or Message never
  // reaches this handler either.
  //
  // kelly-0902-notif-see-more-closes-panel (MEASURED live on web-stg
  // main.31345208.js, 2026-09-03): the SAME unconditional capture caught a
  // SECOND in-feed control that the actor-actions carve-out never covered --
  // "See previous notifications" (PanelFooter, rendered inside FeedBody below
  // the last row per QA-1014 part 9). Clicking it closed the whole panel
  // instead of appending the next page in place (10 rows -> 0, dialog gone),
  // while a sibling PanelBottomBar control (outside FeedBody, e.g. "Mark all
  // as read") correctly left the panel open -- proving this capture handler,
  // not a stale selector, was the culprit. Extend the same marker-based
  // exemption to this subtree rather than special-casing another selector.
  const handleFeedClick = (e) => {
    const t = e.target;
    if (t && t.closest && t.closest('[data-actor-actions="true"], [data-panel-inline-nav="true"]')) return;
    closePanel();
  };

  const bellLabel = unread
    ? `Notifications, ${unread} unread`
    : "Notifications";

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          ref={bellRef}
          onClick={openPanel}
          aria-label={bellLabel}
          aria-haspopup="dialog"
          aria-expanded={open}
          size={isPhone ? "medium" : "large"}
          sx={{
            color: colors.PrimaryPurple,
            // header-tighter-messenger-parity-0916: one fixed 40px box with the
            // same 8px gap as its IconWrapper neighbours at EVERY width. MUI's
            // size="large"/"medium" (50px desktop / 42px phone, measured live on
            // main.43b822a7.js) still set the class, but these win over it.
            width: HEADER_ICON_BUTTON_SIZE,
            height: HEADER_ICON_BUTTON_SIZE,
            padding: `${HEADER_ICON_BUTTON_PADDING_PX}px`,
            margin: `0px ${HEADER_ICON_GAP_PX / 2}px`,
            // gordon-0831-header-bell-hover-circle: match the sibling
            // IconWrapper's rounded-square shape/hover instead of MUI's
            // default circular IconButton hover background.
            borderRadius: "4px",
            "&:hover": {
              backgroundColor: NAV_ICON_HIGHLIGHT_BG,
            },
          }}
        >
          <Badge
            badgeContent={unread}
            max={9}
            overlap="circular"
            data-testid="header-notif-badge"
            // header-tighter-messenger-parity-0916: shared header badge look
            // (18px purple circle, 11px bold count) instead of MUI's stock red.
            slotProps={headerMuiBadgeSlotProps}
          >
            <NotificationsNoneIcon sx={{ fontSize: HEADER_ICON_SIZE }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={bellRef.current}
        onClose={closePanel}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: { borderRadius: "10px", overflow: "hidden", mt: 1 },
        }}
      >
        <Panel role="dialog" aria-label="Notifications">
          <PanelHeader data-testid="notif-panel-header">
            <HeaderLeft>
              <TextView
                variant="subTitle"
                style={{ fontWeight: 700, fontSize: "16px", color: "#141414" }}
              >
                Notifications
              </TextView>
              {unread > 0 && <UnreadPill>{unread}</UnreadPill>}
            </HeaderLeft>
          </PanelHeader>

          {isLoading ? (
            <StateBox aria-busy="true">
              <BrandWheelLoader variant="medium" reroll label="Loading notifications" />
              <TextView style={{ color: "#565556", fontSize: "14px" }}>
                Loading notifications…
              </TextView>
            </StateBox>
          ) : isError ? (
            <StateBox role="alert">
              <TextView
                style={{ color: "#141414", fontWeight: 600, fontSize: "15px" }}
              >
                We couldn't load your notifications.
              </TextView>
              <RetryButton
                type="button"
                onClick={() => refetch()}
                disabled={isFetching}
              >
                {isFetching ? "Retrying…" : "Try again"}
              </RetryButton>
            </StateBox>
          ) : items.length === 0 ? (
            <NotificationsEmptyState />
          ) : (
            <FeedBody onClickCapture={handleFeedClick}>
              {items.map((n) => (
                <NotificationItem
                  key={n.id}
                  name={n.name}
                  image={n.profile_picture}
                  actorProfileImage={n.actor_profile_image}
                  actorUserId={n.actor_user_id}
                  actorName={n.actor_name}
                  wheel={n.image}
                  text={n.description || n.title}
                  type={n.type}
                  wheelName={n.instance_name}
                  time={n.time_ago}
                  isUnread={!n.is_read}
                  isDropDown={true}
                  wheelId={n.ref_id}
                  extras={n.extras}
                  notificationId={n.id}
                  onMarkRead={(id) => markOne.mutate(id)}
                  onDelete={(id) => removeOne.mutate(id)}
                  onReport={
                    n.actor_user_id != null
                      ? () => openReportForActor(n.actor_user_id)
                      : undefined
                  }
                  onActivate={() => {
                    if (!n.is_read) markOne.mutate(n.id);
                  }}
                />
              ))}
              {/* QA-1014 part 9: infinite-scroll driver lives INSIDE the scroll
                  body, below the last row, so appending a page keeps the user
                  exactly where they were (no reset to top). */}
              {hasNextPage && (
                <PanelFooter data-panel-inline-nav="true">
                  <SeePreviousButton
                    type="button"
                    onClick={handleSeePrevious}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage
                      ? "Loading…"
                      : "See previous notifications"}
                  </SeePreviousButton>
                </PanelFooter>
              )}
            </FeedBody>
          )}

          {/* bell-panel-footer-placement-vs-deck: "Mark all as read" + "View
              All" live in a footer bar pinned to the BOTTOM of the panel, per
              the deck (matches the legacy NotificationDropDown's
              ButtonsContainer) — a sibling of FeedBody, not inside its scroll
              region, so it never scrolls out of view. */}
          <PanelBottomBar data-testid="notif-panel-bottom-bar">
            <MarkAllButton
              type="button"
              onClick={() => markAll.mutate()}
              disabled={markAll.isLoading || unread === 0}
            >
              {markAll.isLoading ? "Marking…" : "Mark all as read"}
            </MarkAllButton>
            <ViewAllButton type="button" onClick={handleViewAll}>
              View All
            </ViewAllButton>
          </PanelBottomBar>
        </Panel>
      </Popover>
    </>
  );
}

export default NotificationCenter;
